import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { FormTransformers } from "@/common/utils/formTransformers";
import { useCompleteMaintenanceExecutionObjective } from "@/features/maintenance/features/workflows/features/executionEntries/hooks/useCompleteMaintenanceExecutionObjective";
import { useForwardingTabContext } from "@/features/maintenance/features/workflows/hooks/forwardings/useForwardingTabContext";
import { executionObjectivesCompleteSchema, ExecutionObjectivesCompleteValidationSchema } from "@/features/maintenance/features/workflows/schemas/forwardings/executionObjectives/executionObjectivesComplete.schema";
import { MaintenanceDirectiveObjectiveData } from "@/features/maintenance/features/workflows/types/forwardings/directiveEntries/directiveEntry.type";
import { MaintenanceForwardingsDirectiveEntryKlass } from "@/features/maintenance/features/workflows/types/forwardings/maintenanceForwardingsDirectiveEntryKlasses.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { CarIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface ExecutionObjectiveCompleteProps {
  objective: MaintenanceDirectiveObjectiveData;
}

const ExecutionObjectiveComplete = ({
  objective
}: ExecutionObjectiveCompleteProps) => {
  const { forwarding } = useForwardingTabContext<
    MaintenanceForwardingsDirectiveEntryKlass.ExecutionEntry
  >();

  const [isOpen, setIsOpen] = useState(false);

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const queryClient = useQueryClient();

  const {
    control,
    reset,
    handleSubmit,
    formState: {
      errors,
    },
  } = useForm<ExecutionObjectivesCompleteValidationSchema>({
    resolver: zodResolver(executionObjectivesCompleteSchema),
  });

  const {
    mutateAsync: uploadBlob,
    isPending: uploadBlobIsPending,
  } = useBlobs();

  const {
    mutate: complete,
    isPending: completeIsPending,
  } = useCompleteMaintenanceExecutionObjective({
    objectiveId: objective.id,
    options: {
      onSuccess: () => {
        reset();

        queryClient.invalidateQueries({ queryKey: ['maintenanceForwarding', forwarding.id] });

        setIsOpen(false);
      },
      onError: (error) => {
        FormTransformers.errorsTransformer({
          setMessages,
        })(error);
      },
    },
  });

  const onSubmit: SubmitHandler<ExecutionObjectivesCompleteValidationSchema> = (data) => complete(data);

  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            variant="success"
            operationKlassToCheckPermission="Establishment::Maintenance::ExecutionObjectives::Complete"
          >
            Finalizar
          </Button>
        )}
        title="Anexar foto"
        bodyClassName="text-white-800"
        useCancellableFooter
        titleClass="text-2xl"
        cancelButtonClassName="bg-white-200"
        confirmButtonClassName="bg-primary-blue disabled:opacity-70"
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={completeIsPending}
        disabled={uploadBlobIsPending}
        body={(
          <div className="flex gap-4">
            <MultipleImageUploader
              fieldName="photos_attachments_attributes"
              name="photos_attachments_attributes"
              uploadBlob={uploadBlob}
              label={(
                <p>
                  Foto comprobatória
                </p>
              )}
              control={control}
              inputClassName="size-48 bg-white-200 border-white-200"
              imageCarouselClassName="size-48"
              isLoading={uploadBlobIsPending}
              disabled={uploadBlobIsPending}
              accept=".png, .jpg, .jpeg, .webp"
              uploadButtonLayout="floating"
              errorMessage={errors.photos_attachments_attributes?.message}
            />

            <div className="flex flex-col items-start gap-2">
              <div className="flex">
                <Box className="bg-white-100/15 text-white-800/85 p-2 flex items-center gap-2">
                  <CarIcon strokeWidth={1.5} />

                  <p className="text-sm font-bold">
                    {objective.system.display_name}
                  </p>
                </Box>
              </div>

              <div className="flex flex-col items-start">
                <p className="font-bold text-base">
                  {objective.action.display_name} <span className="font-normal">em</span> {objective.part.display_name}
                </p>

                <p className="text-xs">
                  {objective.action.description}
                </p>
              </div>
            </div>
          </div>
        )}
      />

      <ModalMessageSimple
        messages={messages}
        setMessages={setMessages}
        isError
      />
    </>
  );
}

export default ExecutionObjectiveComplete;
